export type ApprovalStatus = 'approved' | 'denied' | 'review' | 'pending' | null;

export interface ExpirationEntry {
  name: string;
  date: string;
}

export interface ProfileDocument {
  id: string;
  title: string;
  description?: string;
  downloadUrl?: string;
  approvalStatus?: ApprovalStatus;
  createdAt?: string;
  fieldValues?: Record<string, unknown>;
}

/**
 * Extracts expiration entries from field_values.
 *
 * The backend stores expirations as flat key-value pairs in inputFieldsData:
 *   { "Commercial General Liability": "2026-12-10", "Clearance Expiration": "2026-10-15T00:00:00.000Z", ... }
 * All date-valued keys are treated equally — no reserved keys.
 *
 * See: app/components/visitors/document-review-card.gts initExpirationEntries()
 */
export function extractExpirations(fieldValues?: Record<string, unknown>): ExpirationEntry[] {
  if (!fieldValues) return [];

  const metadataKeys = new Set(['issue_date', 'created_at', 'updated_at']);
  const entries: ExpirationEntry[] = [];

  for (const [key, value] of Object.entries(fieldValues)) {
    if (metadataKeys.has(key)) continue;
    if (typeof value !== 'string') continue;
    const parsed = parseDateOnly(value);
    if (isNaN(parsed.getTime())) continue;
    entries.push({ name: key.replace(/_/g, ' '), date: formatExpirationDate(value) });
  }
  return entries;
}

/**
 * Extracts just the date portion from any date string, ignoring time/timezone.
 * Used for expiration dates where we only care about the calendar date.
 * "2026-12-10" and "2026-12-10T00:00:00.000Z" both return local Dec 10.
 */
function parseDateOnly(isoDate: string): Date {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (dateMatch) {
    return new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]));
  }
  return new Date(isoDate);
}

/**
 * Parses a date string preserving time info for full timestamps.
 * Date-only strings ("2026-12-10") are converted to local midnight to avoid off-by-one.
 * Full timestamps ("2026-02-14T14:31:00.000Z") keep their time component.
 */
function parseLocalDate(isoDate: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    const parts = isoDate.split('-').map(Number);
    return new Date(parts[0] as number, (parts[1] as number) - 1, parts[2] as number);
  }
  return new Date(isoDate);
}

function formatExpirationDate(isoDate: string): string {
  try {
    const date = parseDateOnly(isoDate);
    if (isNaN(date.getTime())) return isoDate;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return isoDate;
  }
}

export function formatUploadDate(isoDate?: string): string | undefined {
  if (!isoDate) return undefined;
  try {
    const date = parseLocalDate(isoDate);
    if (isNaN(date.getTime())) return undefined;
    return `Uploaded ${date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  } catch {
    return undefined;
  }
}

export interface StatusDisplay {
  label: string;
  bgClass: string;
  textClass: string;
}

const STATUS_CONFIG: Record<string, StatusDisplay> = {
  pending: { label: 'PENDING', bgClass: 'bg-papaya-10', textClass: 'text-[#A84F00]' },
  review: { label: 'PENDING', bgClass: 'bg-papaya-10', textClass: 'text-[#A84F00]' },
  approved: { label: 'APPROVED', bgClass: 'bg-cilantro-10', textClass: 'text-cilantro-60' },
  denied: { label: 'DENIED', bgClass: 'bg-red-10', textClass: 'text-red-70' },
};

export function formatDetailDate(isoDate?: string): string | undefined {
  if (!isoDate) return undefined;
  try {
    const date = parseLocalDate(isoDate);
    if (isNaN(date.getTime())) return undefined;
    const datePart = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timePart = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
    return `${datePart} \u2022 ${timePart}`;
  } catch {
    return undefined;
  }
}

export function getStatusDisplay(status: ApprovalStatus | undefined): StatusDisplay | null {
  if (!status) return null;
  return STATUS_CONFIG[status] ?? null;
}
